import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Modal } from 'components/Modal';
import { Button, InputAdornment, Stack } from '@mui/material';
import { FormProvider, NumericFormat, TextField } from 'components/Form';
import useYupValidationResolver from 'hook/useYupValidationResolver';

import {
  numericSettingKeys,
  numericSettingValueValidationSchema,
  numericSettingValueOptionalValidationSchema,
  settingValueValidationSchema,
  settingValueOptionalValidationSchema,
} from '../validation';
import { useUpdateSettingValue } from '../api/updateSettingValue';
import { useUpdateCompanySettingValue } from '../api/updateCompanySettingValue';
import { useUpdateFrameworkAgreementSettingValue } from '../api/updateFrameworkAgreementSettingValue';
import { useUpdateDPSSettingValue } from '../api/updateDPSSettingValue';
import { useUpdateDirectSettingValue } from '../api/updateDirectSettingValue';

// The list keeps the short label (section already says "Fees"), but the
// edit field needs to be unambiguous on its own - map a fuller field label
// per key here rather than storing two labels on the backend for one field.
const fieldLabels = {
  job_ad_fee: 'Job ad fee',
};

export const EditSettingModal = ({
  setting,
  open,
  handleClose,
  refetch,
  companyId,
  frameworkAgreementId,
  dpsId,
  directId,
}) => {
  const isOverrideScope = !!companyId || !!frameworkAgreementId || !!dpsId || !!directId;
  const isNumeric = numericSettingKeys.includes(setting?.key);
  // Per-company/per-agreement overrides may be cleared back to empty
  // (reverting to the global default), so they use the "optional" variant
  // of each schema.
  const schema = isNumeric
    ? isOverrideScope
      ? numericSettingValueOptionalValidationSchema
      : numericSettingValueValidationSchema
    : isOverrideScope
    ? settingValueOptionalValidationSchema
    : settingValueValidationSchema;
  const fieldLabel = fieldLabels[setting?.key] ?? setting?.label;

  const methods = useForm({
    defaultValues: { value: '' },
    resolver: useYupValidationResolver(schema),
  });

  const globalMutation = useUpdateSettingValue(setting?.id);
  const companyMutation = useUpdateCompanySettingValue(companyId, setting?.key);
  const frameworkAgreementMutation = useUpdateFrameworkAgreementSettingValue(
    frameworkAgreementId,
    setting?.key
  );
  const dpsMutation = useUpdateDPSSettingValue(dpsId, setting?.key);
  const directMutation = useUpdateDirectSettingValue(directId, setting?.key);
  const { mutateAsync } = companyId
    ? companyMutation
    : frameworkAgreementId
    ? frameworkAgreementMutation
    : dpsId
    ? dpsMutation
    : directId
    ? directMutation
    : globalMutation;

  useEffect(() => {
    if (open && setting) {
      methods.reset({ value: setting.value });
    }
  }, [open, setting]);

  const onMutation = useCallback(() => {
    refetch();
    handleClose();
  }, [refetch, handleClose]);

  const onSubmit = (values) => {
    mutateAsync(values.value ?? '')
      .then(onMutation)
      .catch((error) => console.log(error));
  };

  return (
    <Modal title="Settings edit" open={open} handleClose={handleClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack gap={3} mb={3}>
          {isNumeric ? (
            <NumericFormat
              name="value"
              label={fieldLabel}
              placeholder={`Please enter ${fieldLabel}`}
              prefix=""
              wrapperSx={{ width: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
              }}
              helperText={isOverrideScope ? 'Leave empty to use the global default' : ''}
              required={!isOverrideScope}
            />
          ) : (
            <TextField
              fullWidth
              name="value"
              label={fieldLabel}
              helperText={isOverrideScope ? 'Leave empty to use the global default' : ''}
              required={!isOverrideScope}
            />
          )}
        </Stack>

        <Stack direction="row" gap={3} justifyContent="center">
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Update
          </Button>
        </Stack>
      </FormProvider>
    </Modal>
  );
};
